import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import PageTitle from "../components/PageTitle";
import "./intelex.css";

const authenticationMessages = {
  denied: "Authorization was cancelled. No access was granted.",
  invalid_callback: "The sign-in response was incomplete. Please start again.",
  session_expired:
    "The sign-in attempt expired or could not be verified. Please start again.",
  not_a_pilot: "This vAMSYS account is not registered as a vWZZ pilot.",
  pilot_frozen:
    "This pilot account is currently frozen. Contact vWZZ staff for assistance.",
  pilot_banned: "This pilot account is not permitted to access reporting.",
  wrong_airline: "This pilot account does not belong to vWZZ.",
  not_authorized: "Your account does not currently have access to Interep.",
  failed: "vAMSYS sign-in could not be completed. Please try again.",
};

const interepThemeStorageKey = "interep-theme";

function preferredInterepTheme() {
  try {
    const savedTheme = window.localStorage.getItem(interepThemeStorageKey);
    if (savedTheme === "dark" || savedTheme === "light") return savedTheme;
  } catch {
    // Storage can be unavailable in privacy-focused browser modes.
  }

  return window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function intelexCsrfToken() {
  const names = ["__Host-vwzz_csrf", "vwzz_csrf"];
  const cookies = document.cookie.split(";").map((item) => item.trim());

  for (const name of names) {
    const prefix = `${name}=`;
    const cookie = cookies.find((item) => item.startsWith(prefix));
    if (cookie) return decodeURIComponent(cookie.slice(prefix.length));
  }

  return "";
}

function normalizeBasePath(path) {
  if (!path || path === "/") return "/interep";
  return `/${String(path).replace(/^\/+|\/+$/g, "")}`;
}

function clearAuthenticationMessage(location) {
  const params = new URLSearchParams(location.search);
  if (!params.has("auth")) return;

  params.delete("auth");
  const query = params.toString();
  window.history.replaceState(
    {},
    "",
    `${location.pathname}${query ? `?${query}` : ""}${location.hash || ""}`,
  );
}

function currentReturnPath(location) {
  const params = new URLSearchParams(location.search);
  params.delete("auth");
  const query = params.toString();
  return `${location.pathname}${query ? `?${query}` : ""}`;
}

function ProductMark({ basePath }) {
  return (
    <Link
      className="ix-product-mark"
      to={basePath}
      aria-label="vWZZ Interep reports home"
    >
      <img src="/images/favicon.svg" alt="vWZZ" />
      <span aria-hidden="true" />
      <strong>Interep</strong>
    </Link>
  );
}

function LoginHeader({ basePath }) {
  return (
    <header className="ix-login-header">
      <div className="ix-header-inner">
        <ProductMark basePath={basePath} />
      </div>
    </header>
  );
}

function AuthenticatedHeader({
  basePath,
  user,
  active,
  navItems,
  theme,
  onThemeChange,
  onLogout,
}) {
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const visibleItems = navItems.filter((item) => !item.adminOnly || isAdmin);
  const displayName = user?.username || user?.name || "Pilot";

  return (
    <>
      <header className="ix-app-header">
        <div className="ix-header-inner">
          <ProductMark basePath={basePath} />
          <div className="ix-account">
            <span className="ix-account-name">{displayName}</span>
            <button
              className="ix-theme-toggle"
              type="button"
              role="switch"
              aria-checked={theme === "dark"}
              aria-label="Dark mode"
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              onClick={onThemeChange}
            >
              <span className="ix-theme-toggle-thumb" aria-hidden="true" />
              <svg
                className="ix-theme-toggle-sun"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="3.5" />
                <path d="M12 2v2.2M12 19.8V22M4.93 4.93l1.56 1.56M17.51 17.51l1.56 1.56M2 12h2.2M19.8 12H22M4.93 19.07l1.56-1.56M17.51 6.49l1.56-1.56" />
              </svg>
              <svg
                className="ix-theme-toggle-moon"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M20 15.2A8.3 8.3 0 0 1 8.8 4a8.3 8.3 0 1 0 11.2 11.2Z" />
              </svg>
            </button>
            <button type="button" onClick={onLogout}>
              Sign out
            </button>
          </div>
        </div>
      </header>
      <nav className="ix-navigation" aria-label="Interep reporting navigation">
        <div className="ix-navigation-inner">
          {visibleItems.map((item) => (
            <Link
              key={item.key}
              className={active === item.key ? "active" : ""}
              to={item.to}
              aria-current={active === item.key ? "page" : undefined}
            >
              {item.label}
            </Link>
          ))}
        </div>
      </nav>
    </>
  );
}

export default function IntelexFrame({
  active = "home",
  title = "Interep",
  basePath: requestedBasePath = "/interep",
  navigation,
  testLoginPage = false,
  children,
}) {
  const location = useLocation();
  const basePath = normalizeBasePath(requestedBasePath);
  const [theme, setTheme] = useState(preferredInterepTheme);
  const [auth, setAuth] = useState({
    status: "loading",
    user: null,
    error: "",
  });
  const [testLoginEnabled, setTestLoginEnabled] = useState(null);
  const [testCredentials, setTestCredentials] = useState({
    username: "",
    password: "",
  });
  const [testLoginState, setTestLoginState] = useState({
    working: false,
    error: "",
  });
  const authenticationError = useMemo(
    () => new URLSearchParams(location.search).get("auth"),
    [location.search],
  );
  const navItems = navigation || [
    { key: "home", to: basePath, label: "Reports" },
    { key: "reports", to: `${basePath}/reports`, label: "My reports" },
    {
      key: "review",
      to: `${basePath}/admin/reviews`,
      label: "Review queue",
      adminOnly: true,
    },
    {
      key: "admin",
      to: `${basePath}/admin/templates`,
      label: "Form designer",
      adminOnly: true,
    },
    {
      key: "exams",
      to: `${basePath}/admin/exams`,
      label: "Exam system",
      adminOnly: true,
    },
    {
      key: "settings",
      to: `${basePath}/admin/settings`,
      label: "Access settings",
      adminOnly: true,
    },
  ];

  useEffect(() => {
    document.documentElement.dataset.interepTheme = theme;
    document.documentElement.style.colorScheme = theme;

    try {
      window.localStorage.setItem(interepThemeStorageKey, theme);
    } catch {
      // The active theme still works when persistent storage is unavailable.
    }

    return () => {
      if (document.documentElement.dataset.interepTheme === theme) {
        delete document.documentElement.dataset.interepTheme;
        document.documentElement.style.removeProperty("color-scheme");
      }
    };
  }, [theme]);

  useEffect(() => {
    const syncTheme = (event) => {
      if (
        event.key === interepThemeStorageKey &&
        (event.newValue === "dark" || event.newValue === "light")
      ) {
        setTheme(event.newValue);
      }
    };

    window.addEventListener("storage", syncTheme);
    return () => window.removeEventListener("storage", syncTheme);
  }, []);

  useEffect(() => {
    clearAuthenticationMessage(location);
    const controller = new AbortController();

    fetch("/api/portal/auth/me", {
      credentials: "include",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (response.status === 401) return null;
        if (!response.ok)
          throw new Error("The reporting service is currently unavailable.");
        return response.json();
      })
      .then((payload) =>
        setAuth(
          payload?.user
            ? { status: "authenticated", user: payload.user, error: "" }
            : {
                status: "anonymous",
                user: null,
                error: authenticationMessages[authenticationError] || "",
              },
        ),
      )
      .catch((error) => {
        if (error.name !== "AbortError") {
          setAuth({ status: "anonymous", user: null, error: error.message });
        }
      });

    return () => controller.abort();
  }, [authenticationError, location]);

  useEffect(() => {
    if (!testLoginPage) return undefined;

    const controller = new AbortController();
    fetch("/api/portal/auth/config", {
      credentials: "include",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : {}))
      .then((payload) => setTestLoginEnabled(Boolean(payload.testLoginEnabled)))
      .catch((error) => {
        if (error.name !== "AbortError") setTestLoginEnabled(false);
      });

    return () => controller.abort();
  }, [testLoginPage]);

  const testLogin = async (event) => {
    event.preventDefault();
    setTestLoginState({ working: true, error: "" });
    try {
      const response = await fetch("/api/portal/auth/test-login", {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(testCredentials),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(
          payload.error === "invalid_test_credentials"
            ? "The test username or password is incorrect."
            : "Test sign-in could not be completed.",
        );
      window.location.assign(`${basePath}/exam`);
    } catch (error) {
      setTestLoginState({ working: false, error: error.message });
    }
  };

  const logout = async () => {
    try {
      const response = await fetch("/api/portal/auth/logout", {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": intelexCsrfToken(),
        },
      });

      if (!response.ok && response.status !== 401)
        throw new Error("Could not sign out.");
      setAuth({ status: "anonymous", user: null, error: "" });
    } catch (error) {
      setAuth((current) => ({ ...current, error: error.message }));
    }
  };

  if (auth.status === "loading") {
    return (
      <div className="ix-shell" data-theme={theme}>
        <PageTitle>vWZZ - {title}</PageTitle>
        <LoginHeader basePath={basePath} />
        <main className="ix-session-check" role="status" aria-live="polite">
          <span className="ix-spinner" aria-hidden="true" />
          <h1>Checking your session</h1>
          <p>Please wait while your access is verified.</p>
        </main>
      </div>
    );
  }

  if (auth.status === "anonymous") {
    const returnTo = encodeURIComponent(currentReturnPath(location));

    return (
      <div className="ix-shell ix-login-shell" data-theme={theme}>
        <PageTitle>
          vWZZ - {testLoginPage ? "Interep Test Login" : "Interep Login"}
        </PageTitle>
        <LoginHeader basePath={basePath} />
        <main className="ix-login-main">
          <section className="ix-login-panel" aria-labelledby="ix-login-title">
            <header>
              <h1 id="ix-login-title">
                {testLoginPage ? "Test account sign in" : "Sign in to Interep"}
              </h1>
              <p>
                {testLoginPage
                  ? "Use the local credentials issued for your Interep test account."
                  : "Use your vAMSYS account to continue."}
              </p>
            </header>
            {auth.error && (
              <div className="ix-alert ix-alert-error" role="alert">
                {auth.error}
              </div>
            )}
            {!testLoginPage && (
              <div className="ix-login-content">
                <h2>vAMSYS authentication</h2>
                <p>
                  You will be redirected to vAMSYS to confirm your identity.
                  Your password is never shared with or stored by vWZZ.
                </p>
                <a
                  className="ix-button ix-button-primary ix-login-button"
                  href={`/api/portal/auth/login?return_to=${returnTo}`}
                >
                  Continue to vAMSYS
                </a>
              </div>
            )}
            {testLoginPage && testLoginEnabled === null && (
              <div className="ix-login-content" role="status">
                Checking test account access...
              </div>
            )}
            {testLoginPage && testLoginEnabled === false && (
              <div className="ix-login-content">
                <div className="ix-alert ix-alert-error" role="alert">
                  Test account sign-in is not currently available.
                </div>
              </div>
            )}
            {testLoginPage && testLoginEnabled && (
              <form className="ix-test-login" onSubmit={testLogin}>
                <div>
                  <h2>Test credentials</h2>
                  <p>Enter the username and password issued by an administrator.</p>
                </div>
                <label>
                  <span>Username</span>
                  <input
                    required
                    autoComplete="username"
                    value={testCredentials.username}
                    onChange={(event) =>
                      setTestCredentials((current) => ({
                        ...current,
                        username: event.target.value,
                      }))
                    }
                  />
                </label>
                <label>
                  <span>Password</span>
                  <input
                    required
                    type="password"
                    autoComplete="current-password"
                    value={testCredentials.password}
                    onChange={(event) =>
                      setTestCredentials((current) => ({
                        ...current,
                        password: event.target.value,
                      }))
                    }
                  />
                </label>
                {testLoginState.error && (
                  <div className="ix-alert ix-alert-error" role="alert">
                    {testLoginState.error}
                  </div>
                )}
                <button
                  className="ix-button ix-button-secondary"
                  type="submit"
                  disabled={testLoginState.working}
                >
                  {testLoginState.working
                    ? "Signing in..."
                    : "Sign in as test pilot"}
                </button>
              </form>
            )}
            <footer>
              {testLoginPage
                ? "For authorised local test accounts only."
                : "Access is restricted to authorised vWZZ pilots."}
            </footer>
          </section>
        </main>
        <footer className="ix-public-footer">
          vWZZ Interep · Confidential safety information
        </footer>
      </div>
    );
  }

  const applicant = String(auth.user?.role || "").toLowerCase() === "applicant";
  if (applicant && !location.pathname.startsWith(`${basePath}/exam`)) {
    return <Navigate to={`${basePath}/exam`} replace />;
  }
  const authenticatedNavigation = applicant
    ? [{ key: "exam", to: `${basePath}/exam`, label: "Entry exam" }]
    : navItems;
  const testAccount =
    Boolean(auth.user?.isTestAccount) ||
    String(auth.user?.role || "").toLowerCase() === "test";
  const finalNavigation = testAccount
    ? [
        { key: "home", to: basePath, label: "Report forms" },
        { key: "reports", to: `${basePath}/reports`, label: "My reports" },
        { key: "exam", to: `${basePath}/exam`, label: "Entry exam" },
      ]
    : authenticatedNavigation;

  return (
    <div className="ix-shell" data-theme={theme}>
      <PageTitle>vWZZ - {title}</PageTitle>
      <a className="ix-skip-link" href="#intelex-content">
        Skip to report content
      </a>
      <AuthenticatedHeader
        basePath={basePath}
        user={auth.user}
        active={active}
        navItems={finalNavigation}
        theme={theme}
        onThemeChange={() =>
          setTheme((currentTheme) =>
            currentTheme === "dark" ? "light" : "dark",
          )
        }
        onLogout={logout}
      />
      <main className="ix-main" id="intelex-content">
        {typeof children === "function"
          ? children({
              user: auth.user,
              isAdmin: String(auth.user?.role || "").toLowerCase() === "admin",
            })
          : children}
      </main>
    </div>
  );
}
